import React, { ReactElement } from 'react';
import { useTheme } from 'emotion-theming';
import { Icon, Text, Theme } from '@feast-it/ui/light';

import { useBreakpoint } from '../../hooks/media';
import { Item } from '../Accordion/Item/Item';
import { SliceLayout } from '../SliceLayout/SliceLayout';
import * as Styled from './Accordion.styled';

// eslint-disable-next-line no-unused-vars
type ToggleItemCallback = (heading: string, expanded: boolean) => void;

interface AccordionProps {
  heading: string;
  items: {
    heading: string;
    headingTag: 'h1' | 'h2' | 'h3' | 'h4';
    body: string;
    link?: { url: string };
    linktitle?: string;
  }[];
  displayBackgroundElements?: boolean;
  onToggleItem?: ToggleItemCallback;
  interactive?: boolean;
  defaultExpanded?: boolean;
}
export function Accordion({
  heading,
  items,
  displayBackgroundElements = true,
  onToggleItem,
  interactive = true,
  defaultExpanded = false,
}: AccordionProps): ReactElement {
  const theme: Theme = useTheme();
  const isTabletOrDesktop = useBreakpoint(theme.breakpoints.medium);
  const backgroundIconSize = isTabletOrDesktop ? 820 : 495;

  return (
    <SliceLayout
      backgroundColor="white"
      headingOptions={{ titleText: heading }}
    >
      {displayBackgroundElements && (
        <Styled.BackgroundIconLeft>
          <Icon name="Togather" fill="black" size={backgroundIconSize} />
        </Styled.BackgroundIconLeft>
      )}

      <Styled.Items>
        {items
          .filter(item => item.heading?.trim())
          .map((item, itemKey) => (
            <Item
              heading={item.heading}
              headingTag={item.headingTag}
              key={itemKey}
              onToggle={
                onToggleItem
                  ? (expanded: boolean) => onToggleItem(item.heading, expanded)
                  : undefined
              }
              defaultExpanded={defaultExpanded}
              interactive={interactive}
            >
              <Text style="bodyMedium">{item.body}</Text>
              {item.link && (
                <Styled.colLink href={item.link.url}>
                  {item.linktitle}
                </Styled.colLink>
              )}
            </Item>
          ))}
      </Styled.Items>

      {displayBackgroundElements && (
        <Styled.BackgroundIconRight>
          <Icon name="Togather" fill="black" size={backgroundIconSize} />
        </Styled.BackgroundIconRight>
      )}
    </SliceLayout>
  );
}
